/**
 * TarifRechner — Volumen-Slider, der live zeigt, welcher Tarif (Flexibel
 * oder Flatrate) bei der eingegebenen Anzahl Gutachten/Monat günstiger ist.
 *
 * Modell:
 *   Flexibel = 85 € Grundgebühr  + 75 € × n
 *   Flatrate = 850 € Grundgebühr + 10 € × n
 * Breakeven: n = (850-85) / (75-10) = 11.77 → ~12 Gutachten
 *
 * Auth-aware: Wenn der User eingeloggt ist (z.B. im Onboarding-Flow),
 * werden „Plan wählen"-Buttons angezeigt, die direkt das Backend
 * (`/api/onboarding/select-plan`) aufrufen. Sonst → Link zur Registrierung.
 */
import React, { useMemo, useState } from "react";
import { ArrowRight, Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const FLEX_BASE = 85;
const FLEX_PER = 75;
const FLAT_BASE = 850;
const FLAT_PER = 10;
const BREAKEVEN = Math.ceil((FLAT_BASE - FLEX_BASE) / (FLEX_PER - FLAT_PER));  // 12
const PRESETS = [1, 10, 20, 30, 40];

const fmt = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

export default function TarifRechner() {
  const [n, setN] = useState(10);
  const [selecting, setSelecting] = useState(null);  // "flexibel" | "flatrate" | null
  const { user } = useAuth();
  const navigate = useNavigate();

  const calc = useMemo(() => {
    const flex = FLEX_BASE + n * FLEX_PER;
    const flat = FLAT_BASE + n * FLAT_PER;
    const cheaper = flex <= flat ? "flexibel" : "flatrate";
    const savings = Math.abs(flat - flex);
    return { flex, flat, cheaper, savings };
  }, [n]);

  const selectPlan = async (plan) => {
    if (!user) {
      // nicht eingeloggt → Register-Flow
      navigate(`/register?plan=${plan}`);
      return;
    }
    setSelecting(plan);
    try {
      await api.post("/onboarding/select-plan", { plan });
      toast.success(`${plan === "flexibel" ? "Flexibel" : "Flatrate"} ausgewählt — als Nächstes: Profil vervollständigen.`);
      window.dispatchEvent(new Event("onboarding:refresh"));
      navigate("/settings?tab=profile");
    } catch (e) {
      toast.error(apiError(e, "Plan konnte nicht gespeichert werden."));
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="rounded-3xl border border-cyan-100 bg-gradient-to-br from-cyan-50/60 via-white to-emerald-50/40 p-6 sm:p-10" data-testid="tarif-rechner">
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">Tarif-Rechner</div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
        Wie viele Gutachten machst du im Monat?
      </h2>
      <p className="text-zinc-600 mt-2">
        Verschiebe den Regler — wir zeigen dir live, welcher Tarif günstiger ist.
        <span className="ml-2 inline-block text-cyan-700 font-semibold">Breakeven ≈ {BREAKEVEN} Gutachten / Monat</span>
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-10 items-start">
        {/* Slider + presets */}
        <div className="space-y-6">
          <div>
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">Volumen pro Monat</div>
                <div className="font-display text-5xl font-bold text-zinc-950 mt-1" data-testid="tarif-volume">
                  {n}<span className="text-2xl text-zinc-400 ml-2">Gutachten</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">Dein Vorteil</div>
                <div className={`font-display text-2xl font-bold mt-1 ${calc.savings > 0 ? "text-emerald-600" : "text-zinc-400"}`} data-testid="tarif-savings">
                  {calc.savings > 0 ? `${fmt(calc.savings)} gespart` : "gleich"}
                </div>
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="40"
              step="1"
              value={n}
              onChange={(e) => setN(parseInt(e.target.value, 10) || 1)}
              className="w-full mt-4 accent-cyan-600 h-2"
              data-testid="tarif-slider"
            />
            <div className="flex justify-between mt-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setN(p)}
                  className={`text-xs font-semibold rounded-md px-2 py-1 transition ${
                    n === p ? "bg-cyan-600 text-white" : "text-zinc-500 hover:bg-zinc-100"
                  }`}
                  data-testid={`tarif-preset-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Two cards comparing live */}
          <div className="grid sm:grid-cols-2 gap-4">
            <TarifCard
              name="Flexibel"
              tagline="85 €/Mt. + 75 € pro Gutachten"
              total={calc.flex}
              breakdown={`${FLEX_BASE} € + ${n} × ${FLEX_PER} €`}
              cheaper={calc.cheaper === "flexibel"}
              testid="tarif-card-flexibel"
            />
            <TarifCard
              name="Flatrate"
              tagline="850 €/Monat + 10 €/Gutachten"
              total={calc.flat}
              breakdown={`${FLAT_BASE} € + ${n} × ${FLAT_PER} €`}
              cheaper={calc.cheaper === "flatrate"}
              testid="tarif-card-flatrate"
            />
          </div>

          <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 px-4 py-3 text-sm text-cyan-900" data-testid="tarif-recommendation">
            Bei diesem Volumen ist <strong>{calc.cheaper === "flexibel" ? "Flexibel" : "Flatrate"}</strong> günstiger
            {calc.savings > 0 && <> — du sparst <strong>{fmt(calc.savings)}</strong> im Monat.</>}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => selectPlan("flexibel")}
              disabled={selecting !== null}
              className={calc.cheaper === "flexibel" ? "brand-gradient-bg text-white" : ""}
              variant={calc.cheaper === "flexibel" ? "default" : "outline"}
              data-testid="tarif-cta-flexibel"
            >
              {selecting === "flexibel" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Flexibel wählen <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button
              onClick={() => selectPlan("flatrate")}
              disabled={selecting !== null}
              className={calc.cheaper === "flatrate" ? "brand-gradient-bg text-white" : ""}
              variant={calc.cheaper === "flatrate" ? "default" : "outline"}
              data-testid="tarif-cta-flatrate"
            >
              {selecting === "flatrate" ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Flatrate wählen <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            {!user && (
              <Link to="/preise#vergleich">
                <Button variant="ghost" data-testid="tarif-cta-compare">
                  Vergleich ansehen
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Side info */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 shadow-sm min-w-[260px] max-w-[320px]" data-testid="tarif-info">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-cyan-600 mt-0.5 shrink-0" />
            <div className="text-[13px] text-zinc-700 leading-relaxed">
              <strong>Empfehlung:</strong> Wenn du im Schnitt mehr als <strong>{BREAKEVEN}</strong> Gutachten pro Monat erstellst,
              ist die <strong>Flatrate</strong> günstiger. Bei weniger Volumen lohnt sich <strong>Flexibel</strong>.
            </div>
          </div>
          <hr className="my-4 border-zinc-200" />
          <div className="text-[12px] text-zinc-500 space-y-1.5">
            <div>✓ 2 Tage kostenlos testen</div>
            <div>✓ Alle 3 Monate kündbar</div>
            <div>✓ Tarifwechsel jederzeit möglich</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TarifCard({ name, tagline, total, breakdown, cheaper, testid }) {
  return (
    <div
      className={`rounded-2xl border p-5 transition ${
        cheaper
          ? "border-emerald-300 bg-emerald-50/50 ring-2 ring-emerald-200/60"
          : "border-zinc-200 bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className="font-display font-semibold text-lg">{name}</div>
        {cheaper && (
          <span className="text-[10px] font-bold tracking-widest uppercase bg-emerald-600 text-white rounded-full px-2 py-0.5">
            Günstiger
          </span>
        )}
      </div>
      <div className="text-xs text-zinc-500 mt-0.5">{tagline}</div>
      <div className={`font-display text-3xl font-bold mt-3 ${cheaper ? "text-emerald-700" : "text-zinc-900"}`}>
        {fmt(total)}<span className="text-sm font-normal text-zinc-500"> / Monat</span>
      </div>
      <div className="text-[11px] text-zinc-400 font-mono mt-1">{breakdown}</div>
    </div>
  );
}
